import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '@mui/material';

// Styles object containing all CSS-in-JS definitions for the chat interface
const styles = {
  // Main container for the chat window
  chatContainer: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '350px',
    height: '500px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    zIndex: 1000,
    overflow: 'hidden',
    '@media screen and (maxWidth: 768px)': {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      margin: 0,
      borderRadius: 0,
      maxWidth: '100%',
      maxHeight: '100%'
    }
  },
  // Header section of the chat window
  chatHeader: {
    padding: '15px',
    background: 'linear-gradient(to right, #FF7565, #FF4196)',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'white'
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px', // Space between image and name
  },
  profileImage: {
    width: '32px',
    height: '32px',
    borderRadius: '50%', // Makes the image round
    objectFit: 'cover', // Ensures image covers the space nicely
    border: '2px solid rgba(255, 255, 255, 0.6)', // Subtle white border
  },
  performerName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '500',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 8px',
  },
  // Container for chat messages
  chatMessages: {
    flexGrow: 1,
    padding: '15px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    '@media screen and (maxWidth: 768px)': {
      flex: 1,
      height: 'auto',
      paddingBottom: '80px'
    }
  },
  // Base style for individual messages
  message: {
    margin: '8px 0',
    padding: '12px 16px',
    borderRadius: '20px',
    maxWidth: '80%',
    wordBreak: 'break-word',
    fontSize: '15px',
  },
  // Style for user messages (right-aligned, blue)
  userMessage: {
    backgroundColor: '#C1FFF4',
    color: '#333',
    marginLeft: 'auto'
  },
  // Style for bot messages (left-aligned, grey)
  botMessage: {
    backgroundColor: '#f1f1f1',
    color: '#333',
    marginRight: 'auto'
  },
  // Container for the input form
  chatInputForm: {
    display: 'flex',
    padding: '15px',
    gap: '8px',
    borderTop: '1px solid #eee',
    backgroundColor: '#fff',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%',
    '@media screen and (maxWidth: 768px)': {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      padding: '10px',
      zIndex: 1001
    }
  },
  // Style for the text input field
  chatInput: {
    flexGrow: 1,
    padding: '12px 16px',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    fontSize: '16px',
    backgroundColor: '#f8f8f8',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      borderColor: '#FF7565',
      boxShadow: '0 0 0 1px rgba(255, 117, 101, 0.1)',
    },
    '&:hover': {
      borderColor: '#ddd',
    },
    '&::placeholder': {
      color: '#999',
    }
  },
  // Style for the send button
  sendButton: {
    padding: '8px 24px',
    background: 'linear-gradient(to right, #FF7565, #FF4196)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    minWidth: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    transition: 'transform 0.2s', // Smooth transition for hover effect
    '&:hover': {
      transform: 'scale(1.1)',
    }
  },
};

// Component to handle message rendering with clickable links
const ChatMessages = ({ message }) => {
  // Parse text: handle markdown [text](url) AND bare URLs → clickable links
  const createMarkup = (text) => {
    // First replace markdown links [label](url) with a placeholder pattern
    const mdRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const bareRegex = /(https?:\/\/[^\s<>)"]+)/g;

    const elements = [];
    let lastIndex = 0;

    // Process markdown links first
    const mdMatches = [];
    let m;
    while ((m = mdRegex.exec(text)) !== null) {
      mdMatches.push({ start: m.index, end: m.index + m[0].length, label: m[1], url: m[2] });
    }

    // Process bare URLs (skip positions covered by markdown links)
    const bareMatches = [];
    while ((m = bareRegex.exec(text)) !== null) {
      const inMd = mdMatches.some(md => m.index >= md.start && m.index < md.end);
      if (!inMd) {
        // Strip trailing punctuation
        let url = m[1].replace(/[.,!?)]+$/, '');
        bareMatches.push({ start: m.index, end: m.index + url.length, label: url, url });
      }
    }

    // Merge and sort all matches
    const allMatches = [...mdMatches, ...bareMatches].sort((a, b) => a.start - b.start);

    allMatches.forEach((match, i) => {
      if (match.start > lastIndex) {
        elements.push(text.slice(lastIndex, match.start));
      }
      elements.push(
        <a
          key={i}
          href={match.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#FF4196', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer' }}
          onClick={e => e.stopPropagation()}
        >
          {match.label}
        </a>
      );
      lastIndex = match.end;
    });

    if (lastIndex < text.length) elements.push(text.slice(lastIndex));
    return elements;
  };

  return <div style={{ lineHeight: 1.5 }}>{createMarkup(message.content)}</div>;
};

// Add these SVG components at the top of your file
const HeartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
      fill="url(#heart-gradient)"
    />
    <defs>
      <linearGradient id="heart-gradient" x1="2" y1="3" x2="22" y2="21" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FF7565"/>
        <stop offset="100%" stopColor="#FF4196"/>
      </linearGradient>
    </defs>
  </svg>
);

const SendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M21.426 11.095l-17-8A1 1 0 0 0 3.03 4.242l1.212 4.849L12 12l-7.758 2.909-1.212 4.849a1 1 0 0 0 1.396 1.147l17-8a1 1 0 0 0 0-1.81z"
      fill="url(#heart-gradient)"
    />
    <defs>
      <linearGradient id="heart-gradient" x1="3" y1="3" x2="22" y2="21" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FF7565"/>
        <stop offset="100%" stopColor="#FF4196"/>
      </linearGradient>
    </defs>
  </svg>
);

// Main ChatBot component
const ChatBot = ({ isOpen, onClose, performerName, performer }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [zoeRoomId, setZoeRoomId] = useState(null);
  const messagesEndRef = React.useRef(null);
  const inputRef = useRef(null);
  const isMobile = window.innerWidth <= 768;
  const isZoe = !!(performer && (
    performer.displayName === 'Zoe Harlow' ||
    performer.name === 'Zoe Harlow' ||
    performerName === 'Zoe Harlow' ||
    performerName === 'Zoe'
  ));

  // Get fetishes based on performer data structure
  const getFetishes = (performer) => {
    if (performer.details?.willingnesses) {
      return performer.details.willingnesses;
    }
    return performer.fetishes || [];
  };

  // Define system prompt
  const systemPrompt = performer ? `You are ${performerName}, a live webcam performer flirting in a chat widget on the website instacamgirls.com.

Your profile:
- Name: ${performer.displayName}
- Age: ${performer.age}
- Ethnicity: ${performer.ethnicity}
- Interests: ${getFetishes(performer).join(', ')}

STRICT RULES — never break these:
1. You ONLY exist on instacamgirls.com. NEVER mention Instagram, OnlyFans, Telegram, WhatsApp, or any other platform.
2. NEVER ask for tips, payments, or gifts of any kind. You are not a scam bot.
3. NEVER promise "free videos", content, or anything you cannot deliver.
4. NEVER make up fake offers ("3 free videos", "exclusive content", etc.).
5. Your ONLY call to action is watching the live cam at: https://instacamgirls.com/cam/${performer.displayName}
6. Do NOT share that link in your first 3 messages — flirt and build tension first.
7. First message: warm flirty greeting, ask ONE question about what they're into.
8. Messages 2-3: tease, build desire, stay curious about them.
9. After 3+ exchanges OR when they ask to see you / go private: share the link above.
10. After sharing the link once, include it in every reply after that.
11. Keep ALL responses short — 1-3 sentences max.
12. Stay in character as ${performer.displayName} at all times.` : '';

  // Initialize chat
  useEffect(() => {
    if (!performer?.chatRoomUrl) {
      return; // Exit early if no performer data
    }

    setMessages([
      {
        role: 'system',
        content: systemPrompt
      }
    ]);
    setInputMessage('');
    setIsLoading(false);
  }, [performer, performerName, systemPrompt]);

  // Auto-scroll effect
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Add this useEffect for input focus
  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [isLoading]);

  // Don't render anything if chat is closed or no performer data
  if (!isOpen || !performer) return null;

  // Handle form submission and API communication
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const history = messages
        .filter(m => m.role !== 'system')
        .slice(-6)
        .map(m => ({ role: m.role, content: m.content }));

      let data;
      if (isZoe) {
        // Zoe Harlow — OhAPI digital twin via backend proxy
        let roomId = zoeRoomId;
        if (!roomId) {
          const roomRes = await fetch(`${process.env.REACT_APP_API_URL}/api/zoe/room`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_gender: 'male' }),
          });
          const roomData = await roomRes.json();
          roomId = roomData.room_id;
          setZoeRoomId(roomId);
        }
        const zoeRes = await fetch(`${process.env.REACT_APP_API_URL}/api/zoe/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ room_id: roomId, prompt: inputMessage }),
        });
        const zoeData = await zoeRes.json();
        if (zoeData.room_id) setZoeRoomId(zoeData.room_id);
        data = { reply: zoeData.content };
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat-response`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            message: inputMessage,
            performer: performer?.displayName || performer?.name || '',
            session_id: (() => {
              try {
                let sid = localStorage.getItem('chat_session');
                if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('chat_session', sid); }
                return sid;
              } catch(e) { return 'anon_' + Date.now(); }
            })(),
            lang: new URLSearchParams(window.location.search).get('lg') || navigator.language?.slice(0,2) || 'en',
            device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            history,
          })
        });
        data = await response.json();
      }

      if (data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
        inputRef.current?.focus();
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Sorry, there was an error processing your message.'
      }]);
    } finally {
      setIsLoading(false);
      // Also focus here in case of error
      inputRef.current?.focus();
    }
  };

  // Render chat interface
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        // On mobile: align dialog to bottom of screen (bottom-sheet style)
        ...(isMobile ? {
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
          },
        } : {
          // On desktop: position as fixed bottom-right panel, not a centered modal
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            paddingBottom: '20px',
            paddingRight: '20px',
          },
        }),
        '& .MuiDialog-paper': {
          zIndex: 1500,
          margin: 0,
          ...(isMobile ? {
            width: '100vw',
            maxWidth: '100vw',
            // dvh accounts for keyboard on iOS — shrinks when keyboard appears
            height: '55dvh',
            maxHeight: '55dvh',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
          } : {
            width: '350px',
            height: '500px',
            borderRadius: '12px',
            overflow: 'hidden',
          }),
        },
      }}
      slotProps={{
        backdrop: {
          sx: { zIndex: 1400 },
        },
      }}
    >
      <div style={{
        ...styles.chatContainer,
        // Override fixed positioning — Dialog handles placement now
        position: 'relative',
        bottom: 'auto',
        right: 'auto',
        width: '100%',
        height: '100%',
        borderRadius: 'inherit',
        boxShadow: 'none',
      }}>
        {/* Chat header with title and close button */}
        <div style={styles.chatHeader}>
          <div style={styles.headerInfo}>
            <img 
              src={performer.profilePictureFullUrl} 
              alt=""
              style={styles.profileImage}
            />
            <h3 style={styles.performerName}>{performerName}</h3>
          </div>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        {/* Messages area with filtered system messages */}
        <div style={styles.chatMessages}>
          {messages
            .filter(message => message.role !== 'system')
            .map((message, index) => (
              <div
                key={index}
                style={{
                  ...styles.message,
                  ...(message.role === 'user' ? styles.userMessage : styles.botMessage)
                }}
              >
                <ChatMessages message={message} />
              </div>
            ))}
          {isLoading && (
            <div style={{...styles.message, ...styles.botMessage}}>
              Typing...
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input form for user messages */}
        <form style={styles.chatInputForm} onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type a message..."
            style={styles.chatInput}
            disabled={isLoading}
            onFocus={() => {
              // Scroll messages to bottom when keyboard opens on mobile
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 300);
            }}
          />
          <button 
            type={inputMessage.trim() ? "submit" : "button"}
            style={styles.actionButton}
            onClick={inputMessage.trim() ? null : () => setInputMessage(prev => prev + '❤️')}
            disabled={isLoading}
          >
            {inputMessage.trim() ? <SendIcon /> : <HeartIcon />}
          </button>
        </form>
      </div>
    </Dialog>
  );
};

export default ChatBot;

